import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bibleService } from '../../services/bible';
import { telegramService } from '../../services/telegram';
import LanguageSelector from './LanguageSelector';
import { t } from '../../i18n/uiText';

export default function ParallelVerses({
  verses,
  mainVerse,
  bookName,
  chapter,
  translation = 'synodal',
  onClose,
}) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    // Блокируем скролл body при открытии модального окна
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // Загружаем список книг для текущего перевода
    bibleService.getBooks(translation).then(setBooks);
  }, [translation]);

  useEffect(() => {
    // Закрытие по клику вне модального окна
    const handleClickOutside = (e) => {
      if (e.target.classList.contains('parallel-modal')) {
        onClose();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  const findBookId = (bookName) => {
    const book = books.find(b => b.name === bookName);
    return book ? book.id : null;
  };

  const getLocalizedBookName = (bookId, fallbackName) => {
    const book = books.find((item) => item.id === bookId);
    return book?.name || fallbackName;
  };

  const handleGoToVerse = (e, verse) => {
    e.stopPropagation(); // Предотвращаем закрытие модального окна
    e.preventDefault(); // Предотвращаем стандартное поведение
    
    // Используем bookId напрямую, если он есть, иначе ищем по названию
    const bookId = verse.bookId || findBookId(verse.bookName);
    if (bookId) {
      // Выполняем навигацию к новому стиху
      // Модальное окно закроется автоматически при изменении URL через useEffect в ChapterView
      navigate(`/${translation}/${bookId}/${verse.chapter}/${verse.verse}`);
    } else {
      console.error('BookId not found for:', verse.bookName);
    }
  };

  const handleCopyLink = async () => {
    if (!mainVerse) return;
    
    const isTelegram = telegramService.isTelegramWebApp;
    let link = '';

    // Находим bookId для текущей книги
    const currentBookId = findBookId(bookName);

    if (isTelegram && currentBookId) {
      // Создаем ссылку для Telegram бота с startapp
      // Используем имя бота из конфигурации или по умолчанию
      const botUsername = 'VerbumBibleBot'; // Имя бота из промпта
      const startParam = `${currentBookId}_${chapter}_${mainVerse.verse}_parallel`;
      link = `https://t.me/${botUsername}/app?startapp=${startParam}`;
    } else if (currentBookId) {
      // Создаем обычную веб-ссылку
      link = `${window.location.origin}/${translation}/${currentBookId}/${chapter}/${mainVerse.verse}/parallel`;
    } else {
      alert(t('error'));
      return;
    }

    // В Telegram Mini App используем специальный подход
    if (isTelegram) {
      try {
        // Пробуем использовать Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link);
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } else {
          // Если Clipboard API недоступен, показываем ссылку для ручного копирования
          telegramService.showAlert(`Link:\n${link}\n\n${t('copyManually')}`);
        }
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback: показываем ссылку для ручного копирования
        telegramService.showAlert(`Link:\n${link}\n\n${t('copyManually')}`);
      }
    } else {
      // В обычном браузере
      try {
        await navigator.clipboard.writeText(link);
        setToast(t('linkCopied'));
        setTimeout(() => setToast(null), 2000);
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setToast(t('linkCopied'));
          setTimeout(() => setToast(null), 2000);
        } catch (err) {
          alert(`Link:\n${link}\n\n${t('copyManually')}`);
        }
        document.body.removeChild(textArea);
      }
    }
  };
  
  if (!verses || verses.length === 0) return null;
  
  return (
    <>
      {toast && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-[9999] animate-slide-up">
          <div className="text-white px-6 py-3 rounded-xl shadow-lg font-medium bg-gradient-to-b from-slate-500 via-slate-600 to-slate-700">
            {toast}
          </div>
        </div>
      )}
    <div className="parallel-modal" onClick={onClose}>
        <div className="parallel-content" onClick={(e) => e.stopPropagation()}>
        <div className="bible-modal-sheet-handle" aria-hidden="true" />
        <div className="parallel-header-fixed parallel-header-with-version">
          <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
            <h2 className="text-base font-semibold text-slate-700 shrink-0">
              {t('parallels')}
            </h2>
            <LanguageSelector currentTranslation={translation} />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
              title={t('copyLink')}
              aria-label={t('copyLink')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
            aria-label={t('close')}
          >
            ×
          </button>
          </div>
        </div>
        
        {/* Скроллируемая область */}
        <div className="parallel-scrollable-content">
          {/* Главный стих сверху */}
          {mainVerse && (
            <div className="parallel-main-verse">
              <div className="parallel-main-verse-reference">
                {bookName} {chapter}:{mainVerse.verse}
              </div>
              <div className="parallel-main-verse-text">{mainVerse.text}</div>
            </div>
          )}
          
          {/* Разделитель */}
          {mainVerse && verses.length > 0 && (
            <div className="parallel-divider">
              <div className="parallel-divider-line"></div>
              <span className="parallel-divider-text">{t('parallels')} ({verses.length})</span>
              <div className="parallel-divider-line"></div>
            </div>
          )}
          
          <div className="parallel-list">
            {verses.map((verse, idx) => {
              const bookId = verse.bookId || findBookId(verse.bookName);
              const localizedBookName = getLocalizedBookName(bookId, verse.bookName);
              return (
                <div key={idx} className="parallel-verse-card">
                  <div className="parallel-verse-header">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="parallel-verse-dot"></span>
                      <span className="parallel-verse-reference">{localizedBookName} {verse.chapter}:{verse.verse}</span>
                    </div>
                    {bookId && (
                      <button
                        onClick={(e) => handleGoToVerse(e, verse)}
                        className="parallel-verse-button"
                      >
                        {t('openPassage')}
                      </button>
                    )}
                  </div>
                  <div className="parallel-verse-text">{verse.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

